// src/components/WatchlistPage.jsx
// Three-tab Watchlist: Accounts (monitored carriers) | Competitors | Vendors
// Adding to any tab creates/updates the Notion Account record and triggers Selma enrichment.

import React, { useState, useEffect, useCallback } from 'react';
import './WatchlistPage.css';
import { apiFetch } from '../lib/api';

// The three watch types, described WITHOUT naming one market.
//
// Until 16 Oct 2026 the Accounts tab read "P&C carriers you're monitoring" with
// placeholders naming Amerisure, State Auto and EMC Insurance, and Competitors
// read "Consulting firms competing in your deals" with Deloitte, Accenture and
// Cognizant. For an MGA writing management liability a carrier is who you place
// business WITH, and your competitors are other MGAs — so both tabs described
// somebody else's market to every tenant that is not a P&C consultancy.
//
// The Vendor tab was already neutral ("a platform or tool you work with") and is
// the pattern the other two now follow: name the SHAPE of the thing being
// watched, and let the placeholder say what kind of example belongs there rather
// than supplying three from one industry.
//
// Same rule as exavalu_relevance (migration 0030) and the Cyrano starter prompts:
// nothing in the base may carry one customer's market.
const TABS = [
  {
    key:         'Account',
    label:       'Accounts',
    icon:        '🏢',
    description: "Companies you're monitoring for signals — not actively pursuing.",
    placeholder: 'e.g. a company you want to hear about before you pursue it',
    notesLabel:  'Why watching',
    noteHint:    'e.g. a change you are waiting for, or new leadership',
    color:       'blue',
  },
  {
    key:         'Competitor',
    label:       'Competitors',
    icon:        '🎯',
    description: 'Firms competing in your deals. Selma monitors their wins and moves.',
    placeholder: 'e.g. a firm you lose deals to',
    notesLabel:  'Intel context',
    noteHint:    'e.g. strong practice in your space, recently won a deal you chased',
    color:       'red',
  },
  {
    key:         'Vendor',
    label:       'Vendors',
    icon:        '🔧',
    description: 'Technology vendors in your ecosystem. Selma tracks product launches and customer wins.',
    placeholder: 'e.g. a platform or tool you work with',
    notesLabel:  'Product context',
    noteHint:    'e.g. what they do, and why they matter to you',
    color:       'purple',
  },
];

function AddEditModal({ item, tab, onSave, onCancel }) {
  const [name,    setName]    = useState(item?.name    || '');
  const [notes,   setNotes]   = useState(item?.notes   || '');
  const [website, setWebsite] = useState(item?.website || '');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const isNew = !item;

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ id: item?.id, name: name.trim(), type: tab.key, notes, website });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div className="wl-modal-overlay" onClick={onCancel}>
      <div className="wl-modal" onClick={e => e.stopPropagation()}>
        <div className="wl-modal-header">
          <span className="wl-modal-title">{isNew ? `Add ${tab.label.slice(0,-1)}` : `Edit ${tab.label.slice(0,-1)}`}</span>
          <button className="wl-modal-close" onClick={onCancel}>x</button>
        </div>
        <div className="wl-modal-field">
          <label className="wl-label">Name *</label>
          <input className="wl-input" value={name} onChange={e => setName(e.target.value)} placeholder={tab.placeholder} autoFocus />
        </div>
        <div className="wl-modal-field">
          <label className="wl-label">Website <span className="wl-label-hint">(optional)</span></label>
          <input className="wl-input" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
        </div>
        <div className="wl-modal-field">
          <label className="wl-label">{tab.notesLabel} <span className="wl-label-hint">(optional)</span></label>
          <input className="wl-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder={tab.noteHint} />
        </div>
        {isNew && (
          <div className="wl-modal-selma-note">
            <span>Selma will research this {tab.label.slice(0,-1).toLowerCase()} immediately and write findings to the Account record.</span>
          </div>
        )}
        {error && <div className="wl-error">{error}</div>}
        <div className="wl-modal-footer">
          <button className="wl-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="wl-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? (isNew ? 'Adding...' : 'Saving...') : (isNew ? `Add ${tab.label.slice(0,-1)}` : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

function EntityCard({ item, tab, onToggle, onEdit, onNavigateAccount }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`wl-card ${!item.active ? 'wl-card--inactive' : ''} wl-card--${tab.color}`}>
      <div className="wl-card-main">
        <div className="wl-card-left">
          <div className="wl-card-name">{item.name}</div>
          {item.notes && <div className="wl-card-notes">{item.notes}</div>}
          {item.website && (
            <a className="wl-card-website" href={item.website} target="_blank" rel="noreferrer">
              {item.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          )}
        </div>
        <div className="wl-card-right">
          <div className="wl-card-actions">
            {item.accountId && (
              <button className="wl-account-btn" onClick={() => onNavigateAccount && onNavigateAccount(item.accountId)}>
                Account
              </button>
            )}
            <button className={`wl-toggle ${item.active ? 'wl-toggle--on' : 'wl-toggle--off'}`} onClick={() => onToggle(item)}>
              {item.active ? 'Active' : 'Inactive'}
            </button>
            <button className="wl-edit-btn" onClick={() => onEdit(item)}>Edit</button>
          </div>
          {item.selmaLastResearch && (
            <div className="wl-last-research">
              Selma: {new Date(item.selmaLastResearch).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          )}
        </div>
      </div>
      {item.topFinding && (
        <>
          <button className="wl-intel-toggle" onClick={() => setExpanded(e => !e)}>
            Selma Intel {expanded ? '▲' : '▼'}
          </button>
          {expanded && <div className="wl-intel-body">{item.topFinding}</div>}
        </>
      )}
    </div>
  );
}

function AccountsTab({ onNavigateAccount, showToast }) {
  const [accounts,  setAccounts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showAdd,   setShowAdd]   = useState(false);
  const [enriching, setEnriching] = useState('');
  const tab = TABS[0];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await apiFetch('/api/accounts/list');
      const data = await res.json();
      setAccounts((data.accounts || []).filter(a => a.monitoring));
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async ({ name, notes, website }) => {
    setEnriching(name);
    try {
      const res = await apiFetch('/api/watchlist/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type: 'Account', notes, website }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(`${name} added — Selma is researching`);
      setShowAdd(false);
      setTimeout(() => { load(); setEnriching(''); }, 2000);
    } catch (e) { setEnriching(''); throw e; }
  };

  return (
    <div className="wl-tab-body">
      <div className="wl-tab-description">{tab.description}</div>
      <div className="wl-tab-toolbar">
        <span className="wl-count-pill wl-count-blue">{accounts.length} Monitored</span>
        <button className="wl-add-btn wl-add-btn--blue" onClick={() => setShowAdd(true)}>+ Add Account</button>
      </div>
      {enriching && <div className="wl-enriching-bar">Selma is researching <strong>{enriching}</strong>...</div>}
      {loading && <div className="wl-loading">Loading...</div>}
      {!loading && accounts.length === 0 && (
        <div className="wl-empty">No accounts marked for monitoring. Add one above, or check Monitoring on any Account record.</div>
      )}
      <div className="wl-cards">
        {accounts.map(a => (
          <div key={a.id} className="wl-card wl-card--blue">
            <div className="wl-card-main">
              <div className="wl-card-left">
                <div className="wl-card-name">{a.accountName || a.name}</div>
                <div className="wl-card-meta">
                  {a.tier && <span className="wl-badge">{a.tier}</span>}
                  {a.coreSystem && <span className="wl-badge">{a.coreSystem}</span>}
                </div>
              </div>
              <div className="wl-card-right">
                <button className="wl-account-btn" onClick={() => onNavigateAccount && onNavigateAccount(a.id)}>Open Account</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {showAdd && <AddEditModal item={null} tab={tab} onSave={handleAdd} onCancel={() => setShowAdd(false)} />}
    </div>
  );
}

function WatchlistTab({ tabKey, onNavigateAccount, showToast }) {
  const tab = TABS.find(t => t.key === tabKey);
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [enriching,    setEnriching]    = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await apiFetch('/api/watchlist/list?activeOnly=false');
      const data = await res.json();
      if (data.success) {
        setItems((data.items || []).filter(i => {
          const t = i.type === 'Venor' ? 'Vendor' : i.type;
          return t === tabKey;
        }));
      }
    } catch { } finally { setLoading(false); }
  }, [tabKey]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async ({ name, type, notes, website }) => {
    setEnriching(name);
    try {
      const res = await apiFetch('/api/watchlist/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type, notes, website }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      showToast(`${name} added — Selma is researching`);
      setShowAdd(false);
      await load();
      setTimeout(() => setEnriching(''), 3000);
    } catch (e) { setEnriching(''); throw e; }
  };

  const handleEdit = async ({ id, name, type, notes }) => {
    const res = await apiFetch('/api/watchlist/update', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, name, type, notes }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    showToast(`${name} updated`);
    setEditItem(null);
    await load();
  };

  const handleToggle = async (item) => {
    await apiFetch('/api/watchlist/update', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.id, active: !item.active }),
    });
    showToast(`${item.name} ${!item.active ? 'activated' : 'deactivated'}`);
    await load();
  };

  const visible = showInactive ? items : items.filter(i => i.active);
  const activeCount = items.filter(i => i.active).length;

  return (
    <div className="wl-tab-body">
      <div className="wl-tab-description">{tab.description}</div>
      <div className="wl-tab-toolbar">
        <span className={`wl-count-pill wl-count-${tab.color}`}>{activeCount} Active</span>
        <label className="wl-inactive-toggle">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <button className={`wl-add-btn wl-add-btn--${tab.color}`} onClick={() => setShowAdd(true)}>
          + Add {tab.label.slice(0,-1)}
        </button>
      </div>
      {enriching && <div className="wl-enriching-bar">Selma is researching <strong>{enriching}</strong>...</div>}
      {loading && <div className="wl-loading">Loading...</div>}
      {!loading && visible.length === 0 && (
        <div className="wl-empty">No {tab.label.toLowerCase()} yet. Add one above — Selma will research them immediately.</div>
      )}
      <div className="wl-cards">
        {visible.map(item => (
          <EntityCard
            key={item.id}
            item={{ ...item, type: item.type === 'Venor' ? 'Vendor' : item.type }}
            tab={tab}
            onToggle={handleToggle}
            onEdit={setEditItem}
            onNavigateAccount={onNavigateAccount}
          />
        ))}
      </div>
      {showAdd && <AddEditModal item={null} tab={tab} onSave={handleAdd} onCancel={() => setShowAdd(false)} />}
      {editItem && <AddEditModal item={editItem} tab={tab} onSave={handleEdit} onCancel={() => setEditItem(null)} />}
    </div>
  );
}

export default function WatchlistPage({ onNavigate }) {
  const [activeTab, setActiveTab] = useState('Competitor');
  const [toast,     setToast]     = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleNavigateAccount = (accountId) => {
    if (onNavigate) onNavigate('account-detail', { accountId });
  };

  return (
    <div className="wl-root">
      {toast && <div className="wl-toast">{toast}</div>}
      <div className="wl-header">
        <div className="wl-header-left">
          <h1 className="wl-title">Watchlist</h1>
          <p className="wl-subtitle">Your intelligence radar. Selma monitors everything here nightly and surfaces signals to Victor.</p>
        </div>
      </div>
      <div className="wl-tab-nav">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`wl-tab-btn wl-tab-btn--${tab.color} ${activeTab === tab.key ? 'wl-tab-btn--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="wl-tab-icon">{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'Account'    && <AccountsTab  onNavigateAccount={handleNavigateAccount} showToast={showToast} />}
      {activeTab === 'Competitor' && <WatchlistTab tabKey="Competitor" onNavigateAccount={handleNavigateAccount} showToast={showToast} />}
      {activeTab === 'Vendor'     && <WatchlistTab tabKey="Vendor"     onNavigateAccount={handleNavigateAccount} showToast={showToast} />}
    </div>
  );
}
